import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Button,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    Security,
    CheckCircle,
    Error,
    Warning,
    Refresh,
    Info,
    Business,
    Schedule,
    Fingerprint,
    CloudDone,
    CloudOff,
    History,
    Download,
    Backup,
    Restore
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { green, red, orange, blue, grey } from '@mui/material/colors';
import api from '../services/api';

// =================== STYLED COMPONENTS ===================

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[8],
    },
    backgroundColor: grey[200],
}));

const StatusIcon = styled(Box)(({ status }) => ({
    fontSize: '2rem',
    color: status === 'valid' ? green[500] : 
           status === 'warning' ? orange[500] : 
           status === 'error' ? red[500] : grey[500],
}));

const ProgressCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
    border: `1px solid ${theme.palette.primary.main}30`,
}));

// =================== COMPONENTE PRINCIPAL ===================

export default function LicenseDetailsPage() {
    const [licenseData, setLicenseData] = useState(null);
    const [detailedStatus, setDetailedStatus] = useState(null);
    const [backupList, setBackupList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [showBackups, setShowBackups] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // =================== EFEITOS ===================

    useEffect(() => {
        loadLicenseDetails();
        loadBackupList();
    }, []);

    // =================== FUNÇÕES ===================

    const loadLicenseDetails = async () => {
        try {
            setError(null);

            // Carrega status básico
            const statusResponse = await api.get('/admin/license-status');
            setLicenseData(statusResponse.data);

            // Carrega status detalhado (admin)
            const detailedResponse = await api.get('/admin/license-detailed');

            if (detailedResponse.status === 200) {
            setDetailedStatus(detailedResponse.data);
            }
        } catch (err) {
            console.error('Erro ao carregar detalhes:', err);
            setError('Erro ao carregar informações da licença');
        } finally {
            setLoading(false);
        }
    };

    const loadBackupList = async () => {
        try {
            const response = await api.get('/admin/license-backups');
            if (response.status === 200) {
                setBackupList(response.data.backups || []);
            }
        } catch (err) {
            console.error('Erro ao carregar backups:', err);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadLicenseDetails();
        await loadBackupList();
        setRefreshing(false);
    };

    const handleRestoreBackup = async (filename) => {
        if (!confirm(`Deseja restaurar o backup ${filename}? Esta ação substituirá a licença atual.`)) {
            return;
        }

        setRestoring(true);
        try {
            const response = await api.post('/admin/license-restore', { filename });

            if (response.status === 200) {
            alert('Backup restaurado com sucesso!');
            await handleRefresh();
            setShowBackups(false);
            } else {
            throw new Error(response.data?.detail || 'Erro ao restaurar backup');
            }
        } catch (err) {
            alert(`Erro ao restaurar backup: ${err.response?.data?.detail || err.message}`);
        } finally {
            setRestoring(false);
        }
    };

    // =================== FUNÇÕES AUXILIARES ===================

    const getStatusInfo = () => {
        if (!licenseData) return { status: 'loading', message: 'Carregando...', icon: <CircularProgress size={24} /> };
        
        if (licenseData.valid) {
            const offlineHours = licenseData.offline_hours || 0;
            const tolerance = licenseData.tolerance_hours || 48;
            const remaining = tolerance - offlineHours;
            if (offlineHours < 1) {
                return {
                    status: 'valid',
                    message: 'Licença válida e online',
                    icon: <CheckCircle />,
                    color: green[500]
                };
            } else if (remaining > 6) {
                // offline dentro da tolerância é normal (re-checa online periodicamente)
                return {
                    status: 'valid',
                    message: `Licença válida (verificada há ${offlineHours.toFixed(1)}h)`,
                    icon: <CheckCircle />,
                    color: green[500]
                };
            } else {
                return {
                    status: 'warning',
                    message: `Atenção: ${remaining.toFixed(1)}h para revalidar online`,
                    icon: <Warning />,
                    color: orange[700]
                };
            }
        } else {
            return { 
                status: 'error', 
                message: 'Licença inválida ou expirada', 
                icon: <Error />,
                color: red[500]
            };
        }
    };

    const calculateDaysToExpiry = () => {
        if (!detailedStatus?.expire_date) return null;
        
        try {
            const expireDate = new Date(detailedStatus.expire_date);
            const now = new Date();
            const diffTime = expireDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        } catch {
            return null;
        }
    };

    const getOfflineProgress = () => {
        if (!licenseData) return 0;
        const offline = licenseData.offline_hours || 0;
        const tolerance = licenseData.tolerance_hours || 48;
        return Math.min((offline / tolerance) * 100, 100);
    };

    // =================== RENDER ===================

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                    Tentar Novamente
                </Button>
            }>
                {error}
            </Alert>
        );
    }

    const statusInfo = getStatusInfo();
    const daysToExpiry = calculateDaysToExpiry();
    const offlineProgress = getOfflineProgress();

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabeçalho */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Detalhes da Licença
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Informações completas sobre o licenciamento do sistema
                    </Typography>
                </Box>
                
                <Box display="flex" gap={1}>
                    <Tooltip title="Atualizar informações">
                        <IconButton onClick={handleRefresh} disabled={refreshing}>
                            <Refresh sx={{ 
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </Tooltip>
                    
                    <Button
                        variant="outlined"
                        startIcon={<History />}
                        onClick={() => setShowBackups(true)}
                    >
                        Backups
                    </Button>
                </Box>
            </Box>

            {/* Status Principal */}
            <Card sx={{ mb: 3,
                bgcolor: statusInfo.status === 'valid' ? green[50]
                       : statusInfo.status === 'warning' ? orange[50] : red[50],
                border: `1px solid ${statusInfo.color || green[300]}` }}>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <StatusIcon status={statusInfo.status}>
                                {statusInfo.icon}
                            </StatusIcon>
                            <Box>
                                <Typography variant="h6" component="h2">
                                    {statusInfo.message}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Sistema: {licenseData?.app || 'Hermes'}
                                </Typography>
                            </Box>
                        </Box>

                        <Chip
                            label={licenseData?.valid ? 'ATIVA' : 'INATIVA'}
                            color={licenseData?.valid ? 'success' : 'error'}
                            variant="filled"
                            size="large"
                        />
                    </Box>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Informações Básicas */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <Info sx={{ mr: 1 }} />
                                Informações Básicas
                            </Typography>
                            
                            <List>
                                <ListItem>
                                    <ListItemIcon><Fingerprint /></ListItemIcon>
                                    <ListItemText
                                        primary="Serial"
                                        secondary={
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {detailedStatus?.serial || licenseData?.serial || 'N/A'}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                
                                
                                
                                <ListItem>
                                    <ListItemIcon><Schedule /></ListItemIcon>
                                    <ListItemText
                                        primary="Data de Expiração"
                                        secondary={
                                            <Box>
                                                <Typography variant="body2">
                                                    {detailedStatus?.expire_date || 'N/A'}
                                                </Typography>
                                                {daysToExpiry !== null && (
                                                    <Chip
                                                        size="small"
                                                        label={`${daysToExpiry} dias restantes`}
                                                        color={daysToExpiry > 30 ? 'success' : daysToExpiry > 7 ? 'warning' : 'error'}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Status de Conectividade */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <CloudDone sx={{ mr: 1 }} />
                                Status de Conectividade
                            </Typography>
                            
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body2">Tempo Offline</Typography>
                                    <Typography variant="body2">
                                        {(licenseData?.offline_hours || 0).toFixed(1)}h / {licenseData?.tolerance_hours || 48}h
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={offlineProgress}
                                    color={offlineProgress < 50 ? 'success' : offlineProgress < 80 ? 'warning' : 'error'}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                            
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Última Verificação Online"
                                        secondary={
                                            licenseData?.offline_hours < 1 ? 
                                            'Conectado agora' : 
                                            `${(licenseData?.offline_hours || 0).toFixed(1)} horas atrás`
                                        }
                                    />
                                </ListItem>
                                
                                <ListItem>
                                    <ListItemText
                                        primary="Tolerância Offline"
                                        secondary={`${licenseData?.tolerance_hours || 48} horas`}
                                    />
                                </ListItem>
                                
                                <ListItem>
                                    <ListItemText
                                        primary="Modo Atual"
                                        secondary={
                                            <Chip
                                                size="small"
                                                label={licenseData?.offline_hours < 1 ? 'Online' : 'Offline'}
                                                color={licenseData?.offline_hours < 1 ? 'success' : 'warning'}
                                                icon={licenseData?.offline_hours < 1 ? <CloudDone /> : <CloudOff />}
                                            />
                                        }
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Informações Técnicas */}
                <Grid item xs={12}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <Security sx={{ mr: 1 }} />
                                Informações Técnicas
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' , backgroundColor: grey[100] }}>
                                        <Typography variant="h4" color="primary">
                                            {detailedStatus?.version || '1.0'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Versão da Licença
                                        </Typography>
                                    </Paper>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: grey[100] }}>
                                        <Typography variant="h4" color="secondary">
                                            HMAC-SHA256
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Algoritmo de Assinatura
                                        </Typography>
                                    </Paper>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: grey[100] }}>
                                        <Typography variant="h4" color="info.main">
                                            {detailedStatus?.created_at ? 
                                                new Date(detailedStatus.created_at * 1000).toLocaleDateString() : 
                                                'N/A'
                                            }
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Data de Criação
                                        </Typography>
                                    </Paper>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' , backgroundColor: grey[100] }}>
                                        <Typography variant="h4" color="success.main">
                                            {detailedStatus?.file_status === 'ok' ? '✓' : '✗'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Arquivo de Licença
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Alertas e Recomendações */}
                {(daysToExpiry !== null && daysToExpiry <= 30) || (licenseData?.offline_hours > 24) ? (
                    <Grid item xs={12}>
                        <Alert 
                            severity={daysToExpiry <= 7 || licenseData?.offline_hours > 36 ? 'error' : 'warning'}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Ação Necessária
                            </Typography>
                            <List dense>
                                {daysToExpiry <= 30 && (
                                    <ListItem>
                                        <ListItemText primary={`Licença expira em ${daysToExpiry} dias - Renovação necessária`} />
                                    </ListItem>
                                )}
                                {licenseData?.offline_hours > 24 && (
                                    <ListItem>
                                        <ListItemText primary={`Sistema offline há ${licenseData.offline_hours.toFixed(1)}h - Verifique conectividade`} />
                                    </ListItem>
                                )}
                            </List>
                        </Alert>
                    </Grid>
                ) : null}
            </Grid>

            {/* Dialog de Backups */}
            <Dialog open={showBackups} onClose={() => setShowBackups(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Backup sx={{ mr: 1 }} />
                        Backups de Licença ({backupList.length})
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {backupList.length === 0 ? (
                        <Alert severity="info">Nenhum backup encontrado</Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome do Arquivo</TableCell>
                                        <TableCell>Data de Criação</TableCell>
                                        <TableCell>Tamanho</TableCell>
                                        <TableCell>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {backupList.map((backup) => (
                                        <TableRow key={backup.filename}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {backup.filename}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{backup.created_at_formatted}</TableCell>
                                            <TableCell>{(backup.size / 1024).toFixed(1)} KB</TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    startIcon={<Restore />}
                                                    onClick={() => handleRestoreBackup(backup.filename)}
                                                    disabled={restoring}
                                                >
                                                    Restaurar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBackups(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}